(ns common.util
  (:require
   [clojure.string]
   [common.css           :as css]
   [common.quantity      :as qty]
   [opengb.i18n          :refer [Tr]]
   [reitit.core          :as rtc]
   [tailwind-hiccup.core :refer [tw]]))

(def frontend-requires #{:cljs :graft :re-frame})

(def link-with-filters-attr
  "Marks a link whose url should carry the current filter selections."
  :data-link-with-filters)

(defn ->scion-id
  "Converts a scion-key keyword into a scion-id string."
  [scion-key]
  (str scion-key))

(defn route-k->path
  "Convert a route keyword name (aka. `route-k`) to a string path, based on the
   supplied router."
  ([router route-k]
   (route-k->path router route-k nil))
  ([router route-k path-params]
   (when route-k
     (-> (rtc/match-by-name router route-k path-params)
         rtc/match->path))))

(defn TableRow
  "Provides a row for given title and value. If the value is a quantity then it
  is wrapped in Qty. Optional vectors of tailwind classes can be provided for
  the row and the value. Title can be a translation key (keyword) which will be
  resolved via Tr using ctx."
  [ctx {:keys [title value row-tw value-tw]}]
  [:tr (tw [:group] row-tw)
   [:td (tw [:w-2])]
   [:td [Tr ctx title]]
   [:td (tw [:text-right] value-tw)
    (if (qty/quantity? value)
      [qty/Qty value]
      value)]
   [:td (tw [:w-2])]])

(defn Table
  "Accepts TableRows and an optional title. Title can be a translation key
  resolved via Tr using ctx."
  [ctx {:keys [title]} & rows]
  [:div (tw [:-mx-2 :flex :flex-col :gap-y-3])
   (when title
     [:h2 (tw css/h3) [Tr ctx title]])
   [:table (tw [:w-full :xl:w-auto])
    (into [:tbody] rows)]])

(defn- possibly-namespaced-keyword->str
  "If the keyword is namespaced, preserves the namespace in the string."
  [kw]
  (if (keyword? kw)
    (if-let [ns (namespace kw)]
      (str ns "/" (name kw))
      (name kw))
    kw))

(defn flatten-map
  "Flattens a nested map by concatenating keys with dot.
       e.g. {:a {:b 1 :c 2} :my-ns/d 3 :e {:my-ns/f 4 :g {:h 5}}} -> {:a.b 1, :a.c 2, :my-ns/d 3, :e.my-ns/f 4, :e.g.h 5}"
  ([m]
   (flatten-map m []))
  ([m path-so-far]
   (->> (map (fn [[k v]]
               (if (and (map? v) (not-empty v))
                 (flatten-map v (conj path-so-far k))
                 (let [new-key (->> (conj path-so-far k)
                                    (map possibly-namespaced-keyword->str)
                                    (clojure.string/join ".")
                                    keyword)]
                   [new-key v])))
             m)
        (into {}))))

(defn include-energy-breakdown-in-scorecard?
  "Currently the model output does not include per-end-use information
   for carbon emissions; it only has it for energy.
   So currently we only show breakdown information on the scorecard if the active metric is energy.

   When per-use breakdown information is available for carbon,
   we can get rid of this function and instead update the components that
   render the energy breakdown to instead render the breakdown of whatever map metric is
   currently active, whether that is energy or carbon."
  [ctx]
  (= (:map-metric ctx)
     :total-energy-use))

(defn map-metric->home-score-key
  [map-metric]
  (keyword "score" (name map-metric)))

(defn clamp
  "Constrains x to the inclusive range [lo hi]."
  [x lo hi]
  (-> x
      (max lo)
      (min hi)))

(defn map-metric->roadmap-step-value-key
  [map-metric]
  ({:total-energy-use :rvm/a-total-energy-use
    :ghg-emissions    :rvm/a-total-ghg-emissions}
   map-metric))

(defn rvm-values->net-cost
  "The 'cost after incentives' = full project cost minus incentives.
  `:rvm/b-project-cost` is negative when a project costs money, but the incentive numbers are positive.
  To get project cost minus incentives we negate the project cost and subtract the rebate value."
  [values]
  (qty/q- (qty/qty-fmap #(- (double %)) (:rvm/b-project-cost values))
          (:rvm/d-rebate-value values)))
